import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftRight,
  BedDouble,
  CalendarDays,
  Car,
  Globe2,
  MapPin,
  Package,
  Plane,
  Search,
  Ship,
  Users,
} from 'lucide-react';
import { MAIN_PHONE_DISPLAY, MAIN_PHONE_TEL } from '../../constants/contact';
import { CALL_NOTE } from '../../constants/site';
import { loadWorldPlaces, normalizeText, searchWorldPlaces } from '../../utils/worldPlaces';

export const SEARCH_TABS = [
  { id: 'flights', label: 'Flights', Icon: Plane },
  { id: 'hotels', label: 'Hotels', Icon: BedDouble },
  { id: 'cars', label: 'Cars', Icon: Car },
  { id: 'cruises', label: 'Cruises', Icon: Ship },
  { id: 'packages', label: 'Packages', Icon: Package },
];

/** Cruise departure ports and regions offered as suggestions. */
export const CRUISE_PORTS = [
  'Miami', 'Fort Lauderdale', 'Port Canaveral', 'Galveston', 'New York', 'Seattle', 'Los Angeles',
  'San Juan', 'Vancouver', 'Barcelona', 'Rome', 'Venice', 'Southampton', 'Copenhagen', 'Athens',
  'Dubai', 'Singapore', 'Sydney', 'Tokyo',
];
export const CRUISE_REGIONS = [
  'Caribbean', 'Bahamas', 'Bermuda', 'Mexico', 'Alaska', 'Hawaii', 'Mediterranean', 'Northern Europe',
  'Norwegian Fjords', 'Asia Pacific', 'Australia & New Zealand', 'South America', 'Middle East',
  'Transatlantic',
];
const CAR_TYPES = ['any', 'Economy', 'Compact', 'Midsize', 'SUV', 'Luxury', 'Van', 'Convertible'];

const fmt = (date) => date.toISOString().slice(0, 10);
export const daysFromNow = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return fmt(d);
};

export const buildParams = (obj) =>
  Object.entries(obj)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');

/** "Dehradun, Uttarakhand, India" -> "Dehradun" (catalog search matches on the place name). */
const cityOnly = (label) => label.split(',')[0].trim();

function useWorldPlaces(enabled) {
  const [index, setIndex] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!enabled || index) return undefined;
    let cancelled = false;
    loadWorldPlaces()
      .then((data) => !cancelled && setIndex(data))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [enabled, index]);
  return { index, failed };
}

function searchList(list, query, limit = 8) {
  const q = normalizeText(query);
  const starts = list.filter((v) => normalizeText(v).startsWith(q));
  const contains = list.filter((v) => !starts.includes(v) && normalizeText(v).includes(q));
  return [...starts, ...contains].slice(0, limit).map((v) => ({ name: v, label: v, key: v }));
}

/**
 * Empty text input that suggests places while typing. Uses the world
 * countries/cities list, or a fixed `options` list when given. Free text is
 * still accepted.
 */
function PlaceInput({ value, onChange, placeholder, options: fixedOptions, heading = 'Popular destinations' }) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const [active, setActive] = useState(0);
  const blurTimer = useRef(null);
  const { index, failed } = useWorldPlaces(touched && !fixedOptions);

  const options = useMemo(() => {
    if (fixedOptions) return searchList(fixedOptions, value);
    return searchWorldPlaces(index, value);
  }, [fixedOptions, index, value]);

  const loading = !fixedOptions && touched && !index && !failed;

  const choose = (option) => {
    onChange(option.label);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && options[active]) {
      e.preventDefault();
      choose(options[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const showList = open && (loading || options.length > 0);

  return (
    <div className="fx-s-place">
      <input
        type="text"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        value={value}
        placeholder={placeholder}
        required
        onFocus={() => {
          clearTimeout(blurTimer.current);
          setTouched(true);
          setActive(0);
          setOpen(true);
        }}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setOpen(false), 150);
        }}
        onChange={(e) => {
          onChange(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      {showList && (
        <ul id={listId} className="fx-s-place__list" role="listbox">
          {loading && <li className="fx-s-place__heading">Loading places…</li>}
          {!loading && !value.trim() && <li className="fx-s-place__heading">{heading}</li>}
          {options.map((option, i) => {
            const Icon = option.kind === 'country' ? Globe2 : MapPin;
            return (
              <li
                key={option.label}
                role="option"
                aria-selected={i === active}
                className={`fx-s-place__option${i === active ? ' is-active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(option)}
              >
                <span className="fx-s-place__icon">
                  <Icon size={16} aria-hidden />
                </span>
                <span className="fx-s-place__text">
                  <strong>{option.name}</strong>
                  {option.meta && <small>{option.meta}</small>}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Field({ label, Icon, children }) {
  return (
    <label className="fx-s-field">
      <span className="fx-s-field__label">{label}</span>
      <span className="fx-s-field__control">
        <Icon size={18} aria-hidden />
        {children}
      </span>
    </label>
  );
}

function DateInput({ value, onChange, min }) {
  return <input type="date" value={value} min={min} onChange={(e) => onChange(e.target.value)} required />;
}

function CountSelect({ value, onChange, max = 9, unit }) {
  return (
    <select value={value} onChange={(e) => onChange(Number(e.target.value))}>
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
        <option key={n} value={n}>
          {n} {unit}
          {n > 1 ? 's' : ''}
        </option>
      ))}
    </select>
  );
}

function Choice({ value, onChange, options, labels = {} }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <option key={o} value={o}>
          {labels[o] || o}
        </option>
      ))}
    </select>
  );
}

export default function HeroSearch() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('flights');
  const [flight, setFlight] = useState({ from: '', to: '', depart: '', return: '', adults: 1 });
  const [hotel, setHotel] = useState({ destination: '', checkIn: '', checkOut: '', adults: 2, rooms: 1 });
  const [car, setCar] = useState({ pickup: '', pickupDate: '', dropoffDate: '', carType: 'any' });
  const [cruise, setCruise] = useState({ port: '', destination: '', depart: '', adults: 2 });
  const [pkg, setPkg] = useState({ destination: '', depart: '', return: '', adults: 2 });

  const set = (setter) => (key) => (value) => setter((prev) => ({ ...prev, [key]: value }));
  const setF = set(setFlight);
  const setH = set(setHotel);
  const setC = set(setCar);
  const setCr = set(setCruise);
  const setP = set(setPkg);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (tab === 'flights') {
      navigate(
        `/flight/search/flights?${buildParams({
          ...flight,
          children: 0,
          cabin: 'economy',
          trip: 'roundtrip',
          passengers: String(flight.adults),
        })}`
      );
    } else if (tab === 'hotels') {
      navigate(
        `/flight/search/hotels?${buildParams({
          ...hotel,
          destination: cityOnly(hotel.destination),
          children: 0,
          guests: String(hotel.adults),
        })}`
      );
    } else if (tab === 'cars') {
      const pickup = cityOnly(car.pickup);
      navigate(`/flight/search/cars?${buildParams({ ...car, pickup, dropoff: pickup })}`);
    } else if (tab === 'cruises') {
      navigate(`/flight/search/cruises?${buildParams({ ...cruise, children: 0, guests: String(cruise.adults) })}`);
    } else {
      navigate(
        `/flight/search/packages?${buildParams({
          ...pkg,
          destination: cityOnly(pkg.destination),
          children: 0,
          travelers: String(pkg.adults),
        })}`
      );
    }
  };

  const swapFlight = () => setFlight((f) => ({ ...f, from: f.to, to: f.from }));

  return (
    <div className="fx-s-search">
      <div className="fx-s-search__tabs" role="tablist" aria-label="What would you like to book?">
        {SEARCH_TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`fx-s-search__tab${tab === id ? ' is-active' : ''}`}
            onClick={() => setTab(id)}
          >
            <Icon size={18} aria-hidden />
            {label}
          </button>
        ))}
      </div>

      <form className={`fx-s-search__form fx-s-search__form--${tab}`} onSubmit={handleSubmit}>
        {tab === 'flights' && (
          <>
            <div className="fx-s-route">
              <Field label="From" Icon={MapPin}>
                <PlaceInput value={flight.from} onChange={setF('from')} placeholder="Leaving from" />
              </Field>
              <button type="button" className="fx-s-route__swap" onClick={swapFlight} aria-label="Swap origin and destination">
                <ArrowLeftRight size={16} aria-hidden />
              </button>
              <Field label="To" Icon={MapPin}>
                <PlaceInput value={flight.to} onChange={setF('to')} placeholder="Going to" />
              </Field>
            </div>
            <Field label="Depart" Icon={CalendarDays}>
              <DateInput value={flight.depart} onChange={setF('depart')} min={daysFromNow(0)} />
            </Field>
            <Field label="Return" Icon={CalendarDays}>
              <DateInput value={flight.return} onChange={setF('return')} min={flight.depart || daysFromNow(0)} />
            </Field>
            <Field label="Travelers" Icon={Users}>
              <CountSelect value={flight.adults} onChange={setF('adults')} unit="Adult" />
            </Field>
          </>
        )}

        {tab === 'hotels' && (
          <>
            <Field label="Destination" Icon={MapPin}>
              <PlaceInput value={hotel.destination} onChange={setH('destination')} placeholder="City or country" />
            </Field>
            <Field label="Check-in" Icon={CalendarDays}>
              <DateInput value={hotel.checkIn} onChange={setH('checkIn')} min={daysFromNow(0)} />
            </Field>
            <Field label="Check-out" Icon={CalendarDays}>
              <DateInput value={hotel.checkOut} onChange={setH('checkOut')} min={hotel.checkIn || daysFromNow(0)} />
            </Field>
            <Field label="Guests" Icon={Users}>
              <CountSelect value={hotel.adults} onChange={setH('adults')} unit="Guest" />
            </Field>
            <Field label="Rooms" Icon={BedDouble}>
              <CountSelect value={hotel.rooms} onChange={setH('rooms')} max={5} unit="Room" />
            </Field>
          </>
        )}

        {tab === 'cars' && (
          <>
            <Field label="Pick-up location" Icon={MapPin}>
              <PlaceInput value={car.pickup} onChange={setC('pickup')} placeholder="Pick-up city" />
            </Field>
            <Field label="Pick-up date" Icon={CalendarDays}>
              <DateInput value={car.pickupDate} onChange={setC('pickupDate')} min={daysFromNow(0)} />
            </Field>
            <Field label="Drop-off date" Icon={CalendarDays}>
              <DateInput value={car.dropoffDate} onChange={setC('dropoffDate')} min={car.pickupDate || daysFromNow(0)} />
            </Field>
            <Field label="Car type" Icon={Car}>
              <Choice value={car.carType} onChange={setC('carType')} options={CAR_TYPES} labels={{ any: 'Any type' }} />
            </Field>
          </>
        )}

        {tab === 'cruises' && (
          <>
            <Field label="Departure port" Icon={MapPin}>
              <PlaceInput
                value={cruise.port}
                onChange={setCr('port')}
                options={CRUISE_PORTS}
                heading="Popular ports"
                placeholder="Leaving from"
              />
            </Field>
            <Field label="Cruising to" Icon={Ship}>
              <PlaceInput
                value={cruise.destination}
                onChange={setCr('destination')}
                options={CRUISE_REGIONS}
                heading="Cruise regions"
                placeholder="Region"
              />
            </Field>
            <Field label="Sailing from" Icon={CalendarDays}>
              <DateInput value={cruise.depart} onChange={setCr('depart')} min={daysFromNow(0)} />
            </Field>
            <Field label="Guests" Icon={Users}>
              <CountSelect value={cruise.adults} onChange={setCr('adults')} max={6} unit="Guest" />
            </Field>
          </>
        )}

        {tab === 'packages' && (
          <>
            <Field label="Destination" Icon={MapPin}>
              <PlaceInput value={pkg.destination} onChange={setP('destination')} placeholder="Where do you want to go?" />
            </Field>
            <Field label="Depart" Icon={CalendarDays}>
              <DateInput value={pkg.depart} onChange={setP('depart')} min={daysFromNow(0)} />
            </Field>
            <Field label="Return" Icon={CalendarDays}>
              <DateInput value={pkg.return} onChange={setP('return')} min={pkg.depart || daysFromNow(0)} />
            </Field>
            <Field label="Travelers" Icon={Users}>
              <CountSelect value={pkg.adults} onChange={setP('adults')} unit="Traveler" />
            </Field>
          </>
        )}

        <button type="submit" className="fx-btn fx-btn--accent fx-s-search__submit">
          <Search size={18} aria-hidden />
          Search
        </button>
      </form>

      <p className="fx-s-search__note">
        Results show sample fares for planning; an advisor confirms the live price, rules and any service fee before
        you book. Prefer to talk? Call <a href={`tel:${MAIN_PHONE_TEL}`}>{MAIN_PHONE_DISPLAY}</a>. {CALL_NOTE}
      </p>
    </div>
  );
}
